import React, { FC, useCallback, useMemo, useRef } from 'react';
import { Field, useFormikContext } from 'formik';
import { styled } from 'linaria/react';
import { getTspEventContent, MtsPaymentMethodCard, PaySection, TspPaymentFormProps } from '@features/payment/payment-form';
import { PaymentMethodSwitcher } from '@features/payment/method-switcher';
import { CountrySelect } from '@features/country/select-country';
import { checkIsGameKey, checkIsGameWithRegions, checkIsVoucher, COMPONENTS_BY_NAME, getVisibleTspFields, TspPagePayeeParam, useCyberStore } from '@entities/tsp';
import { DenominationOrderCard, findFirstAvailableDenominationByCountry, resolveDenominationCountry } from '@entities/denomination';
import { PaymentFormValues, PaymentMethod } from '@entities/payment';
import { checkIsMtsClient, getMtsBinding, useAuthDataStore, useBindingsStore } from '@entities/user';
import { paymentLoginMailAddGtm } from '@shared/lib/gtm/redesign';
import { withTheme } from '@shared/lib/theme';
import { Mobile } from '@shared/lib/react';
import { Partners, usePartner } from '@shared/lib/partner';
import { ArrayElement } from '@shared/lib/types';
import { isChromeOrEdge, NoSSR, useIntersectionObserver } from '@shared/lib';
import { Button, InfoBanner } from '@shared/ui/molecules';
import { Link, Text } from '@shared/ui/atoms';
import { SbpIcon } from '@shared/assets/icons';
import AmountInfo from './AmountInfo';
const TspPaymentFormSection: FC<TspPaymentFormProps> = ({
  payee,
  servers,
  countryVouchers,
  denominations,
  pricesByServiceId,
  isGetPaymentTermsLoading,
  isGetPriceLoading,
  isPaymentloading,
  isAmountInfoLoading,
  getCommissions
}) => {
  const {
    paymentMethod,
    activeDenomination,
    activeCountry
  } = useCyberStore();
  const {
    isAuth,
    operatorMTS,
    paymentBanMTS
  } = useAuthDataStore();
  const {
    bindings
  } = useBindingsStore();
  const mtsBinding = getMtsBinding(bindings);
  const isMtsClient = checkIsMtsClient(bindings, isAuth, operatorMTS, paymentBanMTS);
  const {
    partner
  } = usePartner();
  const {
    values,
    validateForm,
    handleBlur
  } = useFormikContext<PaymentFormValues>();
  const fieldWrappersRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [accountDataRef, isAccountDataVisible, accountDataPosition] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0
  });
  const [paySectionRef, isPaySectionVisible, paySectionPosition] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0
  });
  const {
    areAllDenominationsUnavailable,
    isRegionSelectionEnabled
  } = useMemo(() => {
    const country = resolveDenominationCountry(denominations, countryVouchers, activeCountry);
    return {
      areAllDenominationsUnavailable: !findFirstAvailableDenominationByCountry(denominations, country, pricesByServiceId),
      isRegionSelectionEnabled: !denominations.noRegion
    };
  }, [denominations, countryVouchers, activeCountry, pricesByServiceId]);
  const visibleFields = getVisibleTspFields(payee);
  const isGameKey = checkIsGameKey(payee);
  const isVoucher = checkIsVoucher(visibleFields, isGameKey);
  const isGameWithRegions = checkIsGameWithRegions(payee, denominations);
  const getFieldName = (field: TspPagePayeeParam) => isVoucher || isGameKey ? 'email' : field.name;
  const getFieldPlaceholder = (field: TspPagePayeeParam) => field.name === 'server' ? field.description : '';
  const getFieldLabel = (field: TspPagePayeeParam) => {
    if (isVoucher && !isGameKey) {
      return field.title || 'E-mail';
    }
    return field.title || payee?.features?.textAbovePayButton;
  };
  const setFieldRef = useCallback((field: ArrayElement<typeof fields>) => (el: HTMLDivElement | null) => {
    const input = el?.querySelector('input');
    fieldWrappersRefs.current[field.name] = input || el;
  }, []);
  const handleScrollToEmptyField = () => {
    const emptyInput = fieldWrappersRefs.current?.[emptyField?.name as string] as HTMLInputElement;
    emptyInput?.scrollIntoView({
      block: 'center',
      behavior: 'smooth'
    });
    const timerId = setTimeout(() => {
      emptyInput?.focus();
    }, 500);
    return () => clearTimeout(timerId);
  };
  const handleFieldBlur = (field: TspPagePayeeParam) => {
    if (field.name === 'id1' || field.name === 'zone' ||
    // новые
    field.name === 'region' || field.name === 'server' || field.name === 'uid' || field.name === 'uidPubg' || field.name === 'id' || field.name === 'zoneId' || field.name === 'playerId' || field.name === 'email') {
      return async e => {
        // hideTooltip()

        const validationErrors = await validateForm();
        const hasErrors = Object.keys(validationErrors).length > 0;
        const fieldName = getFieldName(field);
        const fieldValue = values[fieldName];
        if (!validationErrors?.[fieldName] && typeof fieldValue === 'string' && fieldValue.trim()) {
          paymentLoginMailAddGtm(getTspEventContent(payee), {
            productName: payee.providerName
          });
        }

        // if (isVoucher || isGameKey) {
        //   if (!validationErrors?.email) {
        //     loginOrEmailGtm(activeService?.providerName)
        //   } else {
        //     loginOrEmailErrorGtm(activeService?.providerName)
        //   }
        // }

        if (!hasErrors && activeDenomination && pricesByServiceId[activeDenomination.serviceId]) {
          getCommissions(pricesByServiceId[activeDenomination.serviceId]);
        }
        handleBlur(e);
      };
    }
  };
  const fields = visibleFields.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)).map(field => ({
    autoComplete: 'off',
    name: getFieldName(field),
    label: getFieldLabel(field),
    placeholder: getFieldPlaceholder(field),
    component: COMPONENTS_BY_NAME[field.name],
    color: 'tertiary',
    options: servers,
    size: {
      desktop: '72',
      mobile: '64'
    },
    onBlur: handleFieldBlur(field)

    // size: 'md',
    // isClearable: IS_CLEARABLE_BY_NAME[field.name],
    // hint: getHint(),
    // tooltip: getToolTip(field),
    // maxLength: field.maxLength,
    // mask: getMask(field),
    // onChange: handleFieldChange(field),
    // onFocus: handleFieldFocus(field),
    // onKeyDown: handleKeyDown,
    // className: 'field-input',
  }));
  const emptyField = fields.find(field => !values[field.name]);
  const shouldShowStickyInputButton = emptyField && !isAccountDataVisible && accountDataPosition !== 'above';
  const shouldShowStickyPayButton = !emptyField && !isPaySectionVisible && paySectionPosition !== 'above' && !shouldShowStickyInputButton;
  return <TspFormSectionWrapper>
      <AccountDataWrapper ref={accountDataRef}>
        <Text family="MTSCompact" weight="medium" size={{
        desktop: 'h6',
        mobile: 'pL'
      }} color="primary">
          Данные аккаунта
        </Text>
        {areAllDenominationsUnavailable && <InfoBanner variant={isRegionSelectionEnabled && countryVouchers.length > 1 ? 'purple' : 'orange'}>
            {isRegionSelectionEnabled && countryVouchers.length > 1 ? <>Номиналов временно нет в&nbsp;наличии в&nbsp;этом регионе. Вы можете выбрать другой регион</> : <>Номиналов временно нет в&nbsp;наличии</>}
          </InfoBanner>}
        {isGameWithRegions && <CountrySelect countryVouchers={countryVouchers} />}
        {!areAllDenominationsUnavailable && fields?.map(field => <FieldWrapper key={field.name} ref={setFieldRef(field)}>
              <Field {...field} />
            </FieldWrapper>)}
      </AccountDataWrapper>
      {!areAllDenominationsUnavailable && <>
          <Mobile>
            <OrderWrapper>
              <Text family="MTSCompact" weight="medium" size={{
            desktop: 'h6',
            mobile: 'pL'
          }} color="primary">
                Ваш заказ
              </Text>
              <DenominationOrderCard payee={payee} denomination={activeDenomination} />
            </OrderWrapper>
          </Mobile>
          {partner === Partners.DEFAULT ? <NoSSR>
              <ReplenishmentMethodWrapper>
                <Text family="MTSCompact" weight="medium" size={{
            desktop: 'h6',
            mobile: 'pL'
          }} color="primary">
                  Способ оплаты
                </Text>
                <PaymentMethodSwitcher payee={payee} />
                {/* {paymentMethod === PaymentMethod.MTS_ACCOUNT && <MtsPaymentMethodCard />} */}
                {paymentMethod === PaymentMethod.MTS_ACCOUNT && isAuth && <MtsPaymentMethodCard />}
              </ReplenishmentMethodWrapper>
            </NoSSR> : <PaymentBySBPWrapper>
              <Text family="MTSCompact" weight="medium" size={{
          desktop: 'h6',
          mobile: 'pL'
        }} color="primary">
                К оплате
              </Text>

              <Text family="MTSCompact" size="pS" weight="medium" color="secondary" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
                Оплата по «Системе быстрых платежей» <SbpIcon />
              </Text>
            </PaymentBySBPWrapper>}
          {(paymentMethod === PaymentMethod.SBP || !isAuth || isMtsClient && mtsBinding?.balance !== undefined) && <>
              <AmountInfoWrapper>
                <AmountInfo isVouchers payee={payee} isGetPaymentTermsLoading={isGetPaymentTermsLoading} isGetPriceLoading={isGetPriceLoading} />
              </AmountInfoWrapper>
              {isChromeOrEdge() && <NoSSR>
                  <InfoBannerWrapper>
                    <InfoBanner>
                      Оплата проходит стабильнее в&nbsp;Яндекс&nbsp;Браузере или&nbsp;с&nbsp;
                      <Link href="https://www.gosuslugi.ru/crt">сертификатами&nbsp;Минцифры</Link>
                    </InfoBanner>
                  </InfoBannerWrapper>
                </NoSSR>}
              <PaySectionWrapper ref={paySectionRef}>
                <PaySection payee={payee} isPaymentloading={isPaymentloading} isAmountInfoLoading={isAmountInfoLoading} isGetPriceLoading={isGetPriceLoading} />
              </PaySectionWrapper>
              {shouldShowStickyInputButton && <Mobile>
                  <StickyWrapper>
                    <Button size={{
              desktop: '72',
              mobile: '52'
            }} onClick={handleScrollToEmptyField}>
                      Ввести {emptyField.label}
                    </Button>
                  </StickyWrapper>
                </Mobile>}
              {shouldShowStickyPayButton && <Mobile>
                  <StickyWrapper>
                    <PaySection payee={payee} isPaymentloading={isPaymentloading} isAmountInfoLoading={isAmountInfoLoading} isGetPriceLoading={isGetPriceLoading} />
                  </StickyWrapper>
                </Mobile>}
            </>}
        </>}
    </TspFormSectionWrapper>;
};
export default TspPaymentFormSection;
const _exp = /*#__PURE__*/() => ({
  theme
}) => theme.colors.background.secondary;
const TspFormSectionWrapper = withTheme(/*#__PURE__*/styled('div')({
  name: "TspFormSectionWrapper",
  class: "tenusgc",
  propsAsIs: false,
  vars: {
    "tenusgc-0": [_exp()]
  }
}));
const AccountDataWrapper = /*#__PURE__*/styled('div')({
  name: "AccountDataWrapper",
  class: "awa3ls2",
  propsAsIs: false
});
const FieldWrapper = /*#__PURE__*/styled('div')({
  name: "FieldWrapper",
  class: "f1055fau",
  propsAsIs: false
});
const OrderWrapper = /*#__PURE__*/styled('div')({
  name: "OrderWrapper",
  class: "o1e9goqp",
  propsAsIs: false
});
const ReplenishmentMethodWrapper = /*#__PURE__*/styled('div')({
  name: "ReplenishmentMethodWrapper",
  class: "rcl2vfl",
  propsAsIs: false
});
const PaymentBySBPWrapper = /*#__PURE__*/styled('div')({
  name: "PaymentBySBPWrapper",
  class: "pue9ppb",
  propsAsIs: false
});
const AmountInfoWrapper = /*#__PURE__*/styled('div')({
  name: "AmountInfoWrapper",
  class: "a41wx8",
  propsAsIs: false
});
const InfoBannerWrapper = /*#__PURE__*/styled('div')({
  name: "InfoBannerWrapper",
  class: "ic4ykjh",
  propsAsIs: false
});
const PaySectionWrapper = /*#__PURE__*/styled('div')({
  name: "PaySectionWrapper",
  class: "p1vm8cdd",
  propsAsIs: false
});
const _exp2 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.background.primary;
const StickyWrapper = withTheme(/*#__PURE__*/styled('div')({
  name: "StickyWrapper",
  class: "s1vd6k4y",
  propsAsIs: false,
  vars: {
    "s1vd6k4y-0": [_exp2()]
  }
}));

require("./TspPaymentFormSection.linaria.module.css!=!../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./TspPaymentFormSection.tsx");